import React, {useCallback, useEffect, useMemo} from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {InputAdornment, TextField} from '@mui/material';

import {COLORS} from '@/styles/colors';
import {isValidEmail, normalizeEmail} from '@/utils/email';

import './styles';

export const TransformTypeInputValues = {
  NONE: 'none',
  LOWERCASE: 'lowercase',
  UPPERCASE: 'uppercase',
} as const;

export const InputTypes = {
  EMAIL: 'email',
  NUMBER: 'number',
};

export type TransformTypeInput =
  (typeof TransformTypeInputValues)[keyof typeof TransformTypeInputValues];

type InputTextFieldProps = {
  label: string;
  placeholder?: string;
  value: string;
  setValue?: React.Dispatch<React.SetStateAction<string>>;
  style?: React.CSSProperties;
  type?: string;
  autoComplete?: string;
  autofocus?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperError?: string;
  helperText?: string;
  maxLength?: number;
  labelBackgroundColor?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onClick?: () => void;
  onPaste?: (event: any) => void;
  inputRef?: React.Ref<any>;
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
  id?: string;
  maskedInput?: React.ForwardRefExoticComponent<
    Omit<any, 'ref'> & React.RefAttributes<HTMLInputElement>
  >;
  transformType?: TransformTypeInput;
  min?: number;
  onErrorChange?: (hasError: boolean) => void;
};

const InputTextField: React.FC<InputTextFieldProps> = ({
  label,
  value,
  placeholder,
  setValue,
  style: styleProps,
  type = 'text',
  autoComplete = 'off',
  autofocus = false,
  disabled = false,
  error = false,
  helperError,
  helperText,
  maxLength = 100,
  onChange,
  onKeyDown,
  onBlur,
  onClick,
  onPaste,
  inputRef,
  onInput,
  id,
  maskedInput,
  transformType = TransformTypeInputValues.NONE,
  min,
  onErrorChange,
  labelBackgroundColor,
}) => {
  const [internalError, setInternalError] = React.useState(false);
  const [internalHelperText, setInternalHelperText] = React.useState('');

  const isEmailType = useMemo(() => type === InputTypes.EMAIL, [type]);

  const validateEmail = useCallback((newValue: string): string => {
    const isValidEmailValue = isValidEmail(newValue);

    if (!isValidEmailValue && newValue) {
      setInternalError(true);
      setInternalHelperText('Please provide a valid email');
      return newValue;
    }

    setInternalError(false);
    setInternalHelperText('');
    return newValue;
  }, []);

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange(internalError);
    }
  }, [internalError, onErrorChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;
      if (transformType === TransformTypeInputValues.UPPERCASE) {
        newValue = newValue.toUpperCase();
      } else if (transformType === TransformTypeInputValues.LOWERCASE) {
        newValue = newValue.toLowerCase();
      }
      let outputValue = newValue;
      if (newValue.length > maxLength) return;

      if (isEmailType) {
        newValue = normalizeEmail(newValue);
        outputValue = validateEmail(newValue);
      }

      setValue?.(outputValue);
    },
    [isEmailType, maxLength, setValue, transformType, validateEmail],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isEmailType && event.key === ' ') {
        event.preventDefault();
      }
    },
    [isEmailType],
  );

  const handleOnBlur = useCallback(() => {
    onBlur && onBlur();
  }, [onBlur]);

  const inputProps = {
    maxLength: maxLength,
    style: {
      fontSize: '16px',
      fontWeight: 400,
      padding: '16px 0 16px 16px',
    },
    ...(min !== undefined && {min}),
  };

  useEffect(() => {
    if (isEmailType) {
      if (!value) {
        setInternalError(false);
        setInternalHelperText('');
      }
    }
  }, [isEmailType, type, validateEmail, value]);

  useEffect(() => {
    if (
      type === InputTypes.NUMBER &&
      inputRef &&
      typeof inputRef !== 'function' &&
      'current' in inputRef &&
      inputRef.current
    ) {
      const inputEl = inputRef.current;

      const preventScroll = (e: WheelEvent) => {
        e.preventDefault();
      };

      inputEl.addEventListener('wheel', preventScroll, {passive: false});

      return () => {
        inputEl.removeEventListener('wheel', preventScroll);
      };
    }
  }, [inputRef, type]);

  return (
    <TextField
      id={id}
      autoFocus={!!autofocus}
      className="text-field"
      label={label}
      variant="outlined"
      style={{width: '100%', ...styleProps}}
      placeholder={placeholder}
      value={value}
      type={type}
      autoComplete={autoComplete}
      onClick={onClick ?? onClick}
      onKeyDown={onKeyDown ?? handleKeyDown}
      onChange={onChange ?? handleChange}
      onInput={onInput}
      onBlur={handleOnBlur}
      onPaste={onPaste}
      disabled={disabled}
      inputProps={inputProps}
      inputRef={inputRef}
      error={!!internalError || !!error}
      helperText={
        internalError
          ? internalHelperText
          : error && helperError
            ? helperError
            : helperText
      }
      FormHelperTextProps={{
        sx: {
          fontSize: '12px',
          maxWidth: '80%',
          marginTop: '4px',
          marginLeft: '16px',
          fontWeight: '400',
          lineHeight: '14px',
        },
      }}
      InputLabelProps={{
        sx: {
          fontSize: '16px',
          transform: 'translate(16px, 16px) scale(1)',
          '&.MuiInputLabel-shrink': {
            fontSize: '14px',
            padding: '10px 5px 0px 0px',
            margin: '0px 0px 0px 16px',
            transform: 'translate(0, -1.5rem) scale(0.75)',
            backgroundColor: labelBackgroundColor ?? COLORS.WHITE,
          },
        },
      }}
      InputProps={{
        sx: {
          borderRadius: '10px',
          height: '56px',
        },
        inputComponent: maskedInput ?? undefined,
        endAdornment: (internalError || error) && (
          <InputAdornment position="end">
            <ErrorOutlineIcon color="error" />
          </InputAdornment>
        ),
      }}
    />
  );
};

export default InputTextField;
